/** @jsxImportSource ./simboluri-jsx */
// Studio — vizualizatorul structurii (mock-ui/design/Structura3D.dc.html și
// artboard-ul «Structura» din macheta platformei): planșa pe tot ecranul, cu
// UN SINGUR meniu plutitor în stânga (Corecția lui Stefan, 25.09). Sus,
// soluția — numele, fazele, acțiunile (`nav/BlocSolutie`); dedesubt grupurile
// vederii: Corp (cu starea fiecăruia), Vedere (Model 3D · Moduri · Încărcări ·
// Componente · Lanțul de calcul · Setări de calcul), Nivel, Afișare.
//
// Vederea stă în URL (`?vedere=`, contract §8), implicit «Model 3D»: cât
// rândul e în lucru, locul modelului îl ține scheletul clădirii. Corpul stă
// în `?corp=`.
//
// Refolosește Scene3D + PanouProprietati din StepFem — aceeași scenă, alt
// chrome: aici e SUPRAFAȚA DE PREZENTARE, pagina Pasului 7 rămâne pagina de
// verificare a modelului (expert).

import { cheiaFundatiei, fundatiaAdoptata } from "./fundatia";
import { numar, useT, useTn } from "../i18n";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { preincarcaDesen, preincarcaEditorul, preincarcaForme, stepDrawingUrl } from "./apiStructura";
import { useSchitaFem } from "./schitaFem";
import { useModelComplex } from "./modelComplex";
import { indexParter } from "./elemente";
import { Scene3D, PanouProprietati, SelectieFem, FormeModale } from "./StepFem";
import { indexClase } from "./claseElemente";
import Incarcari3D, { Mod3D } from "./Incarcari3D";
import FisaIncarcare from "./FisaIncarcare";
import StudioSumar, { pasiSelectie, refDinSelectie } from "./StudioSumar";
import StudioPlan from "./StudioPlan";
import { verdictAx } from "./DrawerAxFundatie";
import { AxFundatie } from "./tipuri";
import { AvertismentMetoda, CazLegenda, FemResult, MasaNivel,
         StepRezumat, StepState } from "./tipuri";
import { hashElement, hashSectiune, hashStudio, refLegatura } from "./legaturi";
import type { Silueta } from "../api";
import ScheletIncarcare from "./ScheletIncarcare";
import { stareModel } from "./schelet";
import { useStructure } from "./StructureContext";
import { useSearchParams } from "react-router-dom";
import EditorStructura from "./EditorStructura";
import { useRolProiect } from "../components/rol";
import PipelinePage from "./PipelinePage";
import Preferinte from "./Preferinte";
import BlocSolutie from "../nav/BlocSolutie";
import { useRegistru } from "../nav/Registru";
import { useActiuni } from "../nav/actiuni";
import { SemnStare } from "../components/FazeBara";
import { caleStructuraFatada, CORP_TOATE, actiuneaLantului, caleRand, elementulRandului, faraVerdict, numeElement,
         numeRand, numeStare, picateGlobaleCorp, picateGlobaleRand, VEDERI_STRUCTURA,
         type VedereStructura } from "../nav/selectie";
import { VedereComponente } from "./Componente";
import PlanArmare from "./PlanArmare";
import PlanCofraj from "./PlanCofraj";
import PlanSapatura from "./PlanSapatura";
import { traduceRezultat } from "./texteRezultat";

function nrRo(v: number, zecimale = 0): string {
  return numar(v, {
    minimumFractionDigits: zecimale,
    maximumFractionDigits: zecimale,
  });
}

// pașii de armare: fără niciunul rulat, rândul n-are armătură de arătat
const PASI_ARMARE = ["armare_grinzi", "armare_stalpi", "armare_pereti",
                     "armare_placa", "armare_fundatie"];

// VEDEREA ÎNTREGULUI COMPLEX («Toate corpurile»): piesele poartă corpul lor,
// iar id-urile de plan sunt deplasate cu 1000·k pe corp (`complex_corpuri`,
// aceeași regulă ca în ansamblu — `corpuri.renumeroteaza_id`). Peretele
// exterior al parcării e al corpului fictiv «parcare».
const CORP_PARCARE = "parcare";
const PAS_ID_CORP = 1000;

/** Piesa aleasă în vederea complexului: al cărui corp e și id-ul ei DE PLAN
 *  în corpul acela — pagina, verdictele și armarea ei stau acolo. */
export function piesaComplex(r: FemResult, sel: SelectieFem):
    { corp: string; ref: string | null } | null {
  if (!sel) return null;
  const m = r.model;
  const piesa = sel.tip === "stalp" ? m.stalpi?.[sel.ref]
    : sel.tip === "grinda" ? m.grinzi?.[sel.ref]
    : sel.tip === "perete_beton" ? m.pereti_beton?.[sel.ref]
    // radierul unui corp și placa pe sol (a unui corp pe tălpi sau a
    // parcării) poartă și ele corpul
    : sel.tip === "radier" ? m.radiere?.[sel.ref]
    : sel.tip === "placa_pe_sol" ? m.placa_pe_sol?.elemente?.[sel.ref]
    : null;
  const corp = (piesa as { corp?: string } | undefined)?.corp;
  if (!corp) return null;
  const k = (r.corpuri ?? []).findIndex((c) => c.cod === corp);
  const idComplex = refDinSelectie(r, sel);
  if (!idComplex || k <= 0) return { corp, ref: idComplex };
  const m2 = /^([A-Za-z]+)(\d+)(.*)$/.exec(idComplex);
  const ref = m2 ? `${m2[1]}${Number(m2[2]) - k * PAS_ID_CORP}${m2[3]}` : idComplex;
  return { corp, ref };
}

/** Capul drawer-ului pe «Toate corpurile»: piesa e a unui corp, iar pagina
 *  ei, verdictele și armarea stau pe corpul acela — de aici se trece la el.
 *  Peretele parcării n-are pagină: e desenat, nu calculat. */
export function CardCorpComplex({ id, solutie, r, sel, onClose }: {
  id: string;
  solutie: string | null;
  r: FemResult;
  sel: SelectieFem;
  onClose: () => void;
}) {
  const t = useT();
  const p = piesaComplex(r, sel);
  if (!p) return null;
  const parcare = p.corp === CORP_PARCARE;
  const placa = sel?.tip === "placa_pe_sol";
  const radier = sel?.tip === "radier";
  const refCorp = refLegatura(solutie, p.corp);
  const nota = parcare
    ? t(placa ? "struct.studio.complex_placa_parcare" : "struct.studio.complex_parcare")
    : radier ? t("struct.studio.complex_radier") : t("struct.studio.complex_nota");
  return (
    <div className="ob-studio-sumar" data-testid="card-complex">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <b className="ob-disp" style={{ fontSize: 17 }}>
          {parcare ? t(placa ? "struct.fem.lay.placa_sol" : "struct.fem.lay.pereti_beton")
                   : radier ? t("struct.studio.complex_radier_corp", { corp: p.corp })
                   : t("struct.studio.complex_corp", { corp: p.corp })}
        </b>
        {p.ref && (
          <span className="ob-chip ob-c-mut" style={{ fontSize: 10 }}>{p.ref}</span>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn" onClick={onClose}
                aria-label={t("comutator.inchide")}>×</button>
      </div>
      <p className="sg-muted" style={{ fontSize: 12, marginTop: 6 }}>
        {nota}
      </p>
      {!parcare && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          <a className="ob-chip ob-c-acc" style={{ textDecoration: "none" }}
             href={hashStudio(id, refCorp)}>
            {t("struct.studio.complex_deschide", { corp: p.corp })}
          </a>
          {p.ref && (
            <a className="ob-chip ob-c-mut" style={{ textDecoration: "none" }}
               href={hashElement(id, p.ref, refCorp)}>
              {t("struct.studio.complex_element", { ref: p.ref })}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// FUNDAȚIA NU E UN TAB: în 3D e betonul de sub clădire;
// în 2D e o VEDERE a planului, cu buton propriu în banda de niveluri (separată
// de subsol) și drawer-ul de ax la click. `?vedere=fundatie` deschide
// Studioul în 2D, direct pe vederea de fundație.
type TabStudio = "model" | "moduri" | "incarcari";
// proiecția vederii de model: volumul 3D sau planul nivelului (Pasul 4)
type Proiectie = "3d" | "2d";

export default function Studio({
  id,
  cloneId,
  solutie,
  steps,
  rezumate,
  silueta,
}: {
  id: string;
  // spațiul din care se citesc datele (corpul rândului privit): extrasul de
  // armătură al elementului selectat se cere de la el
  cloneId?: string | null;
  /** referința de legătură a rândului privit (v. `legaturi.refLegatura`) */
  solutie?: string | null;
  steps: Record<string, StepState>;
  // rezumatele pașilor de pe server (StructureContext): cu ele se deosebește
  // «rezultatul Pasului 7 e pe drum» de «nu există» — fără ele, orice
  // încărcare arăta ca o lipsă
  rezumate?: Record<string, StepRezumat> | null;
  // conturul nivelurilor din soluția de arhitectură: din el se desenează
  // scheletul clădirii cât timp se încarcă modelul
  silueta?: Silueta | null;
}) {
  const t = useT();
  const navigate = useNavigate();
  const [sel, setSel] = useState<SelectieFem>(null);
  const [params, setParams] = useSearchParams();
  const { corpuri, solutiaCurenta, runError, opreste, lant,
          cerePasi, toateCorpurile } = useStructure();
  const tn = useTn();
  const { registru, reciteste, anunta } = useRegistru();
  // «Lanțul de calcul» pornește faza rândului prin ACȚIUNILE soluției, cu
  // regulile și confirmarea cascadei de acolo (v. `nav/actiuni`)
  const actiuni = useActiuni();
  const actiuneLant = registru ? actiuneaLantului(registru.solutii, solutiaCurenta) : null;
  const arhitecturaRandului = solutiaCurenta?.lineage.architecture ?? null;
  const pornesteLantul = () => {
    if (!actiuneLant) return;
    if (actiuneLant.fel === "reia_schita") void actiuni.reiaSchita(actiuneLant.arhitectura);
    else if (actiuneLant.fel === "reruleaza") void actiuni.reruleaza(actiuneLant.macro);
    else void actiuni.pornesteDetaliat(actiuneLant.macro);
  };
  // VEDEREA, din URL; fără `?vedere=` (sau cu una care nu mai există),
  // «Model 3D»
  const cerut = params.get("vedere");
  const vedere: VedereStructura | "fundatie" =
    VEDERI_STRUCTURA.find((v) => v === cerut)
      ?? (cerut === "fundatie" ? "fundatie" : "model");
  const tab: TabStudio | null = vedere === "moduri" ? "moduri"
    : vedere === "incarcari" ? "incarcari"
    : vedere === "model" || vedere === "fundatie" ? "model" : null;
  const alegeVederea = (v: VedereStructura) => {
    const q = new URLSearchParams(params);
    q.set("vedere", v);
    setParams(q);
  };
  const [proiectie, setProiectie] = useState<Proiectie>(
    vedere === "fundatie" ? "2d" : "3d");
  // modul vizualizării de încărcări: masa seismică sau un caz anume
  const [mod3d, setMod3d] = useState<Mod3D>({ fel: "masa" });
  // Nivelul planului 2D, ales din meniu: indexul etajului Pasului 4 sau
  // fundația. `null` = încă neales, deci parterul (v. `StudioPlan`).
  // unde își scriu straturile scena 3D și desenul 2D — grupul «Afișare»
  const [gazdaStraturi, setGazdaStraturi] = useState<HTMLDivElement | null>(null);
  const [vedere2d, setVedere2d] = useState<number | "fundatie" | null>(
    vedere === "fundatie" ? "fundatie" : null);
  // «EDITEAZĂ GEOMETRIA MACRO» (A8): editorul se deschide pe rândul privit,
  // care rămâne sursa; ciorna stă aici, iar «Salvează ca Soluția N+1» naște
  // rândul nou și duce pe el.
  const [editez, setEditez] = useState(false);
  useEffect(() => setEditez(false), [solutiaCurenta?.id]);
  // Deschiderea editorului durează: se cere dinainte, cât privești o soluție
  // gata pe care o poți edita, ca «Editează» să treacă pe loc (`deschideEditor`).
  const { potScrie } = useRolProiect();
  const sursaEditabila = potScrie && solutiaCurenta?.status === "ready" ? solutiaCurenta.id : null;
  useEffect(() => {
    if (!sursaEditabila) return;
    const tm = setTimeout(() => preincarcaEditorul(id, sursaEditabila), 1200);
    return () => clearTimeout(tm);
  }, [id, sursaEditabila]);

  // BULINA unui corp: câte critice și globale pică pe el
  // (`picateGlobaleCorp`); pe «Toate corpurile», totalul soluției — aceeași
  // cifră ca pe «Verificări» din șină. Nimic cât rândul e deschis.
  const bulina = (n: number | null) => (n ? (
    <span className="corp-bulina" aria-hidden="true">{n > 99 ? "99+" : n}</span>
  ) : null);
  const cuPicate = (text: string, n: number | null, cod: string) =>
    n ? `${text} · ${tn("struct.corpuri.picate#n", n, { cod })}` : text;
  const alegeCorpul = (cod: string) => {
    const q = new URLSearchParams(params);
    q.set("corp", cod);
    setParams(q);
  };
  const totalPicate = picateGlobaleRand(solutiaCurenta);
  /** Grupul «Corp»: întâi «Toate corpurile» — întregul complex, cu totalul
   *  picatelor —, apoi fiecare corp cu starea (A6) și cifra lui. Alegerea
   *  scrie `?corp=`. */
  const grupCorp = corpuri.length > 1 && (
    <span className="grup-vederi">
      <span className="lbl">{t("viz.grup.corp")}</span>
      <div className="meniu-corpuri" role="group" aria-label={t("struct.corpuri.aria")}>
        <button type="button" className={toateCorpurile ? "on" : ""}
                aria-pressed={!!toateCorpurile}
                aria-label={totalPicate
                  ? tn("struct.corpuri.picate#n", totalPicate, { cod: t("viz.toate_corpurile") })
                  : undefined}
                title={cuPicate(t("struct.corpuri.toate_titlu"), totalPicate,
                                t("viz.toate_corpurile"))}
                onClick={() => alegeCorpul(CORP_TOATE)}>
          {t("viz.toate_corpurile")}
          {bulina(totalPicate)}
        </button>
        {corpuri.map((c) => {
          const n = picateGlobaleCorp(solutiaCurenta, c);
          const nume = c.ansamblu ? t("struct.corpuri.ansamblu") : c.cod;
          const stare = c.motiv ? `${numeStare(faraVerdict(c.stare))} — ${c.motiv}`
            : numeStare(faraVerdict(c.stare));
          return (
            <button key={c.cod} type="button" className={c.activ ? "on" : ""}
                    aria-pressed={c.activ}
                    aria-label={n ? tn("struct.corpuri.picate#n", n, { cod: nume }) : undefined}
                    title={cuPicate(stare, n, nume)}
                    onClick={() => alegeCorpul(c.cod)}>
              <SemnStare stare={faraVerdict(c.stare)} s={12} />
              {nume}
              {bulina(n)}
            </button>
          );
        })}
      </div>
    </span>
  );

  const blocSolutie = solutiaCurenta && (
    <BlocSolutie rand={solutiaCurenta}
                 corp={corpuri.length > 1
                   ? (toateCorpurile ? CORP_TOATE : corpuri.find((c) => c.activ)?.cod ?? null)
                   : null}
                 // complexul nu se editează: geometria e a fiecărui corp
                 laEditeaza={toateCorpurile ? undefined : () => setEditez(true)}
                 laPregatesteEditarea={sursaEditabila && !toateCorpurile
                   ? () => preincarcaEditorul(id, sursaEditabila) : undefined} />
  );

  // MENIUL DIN STÂNGA, unul singur, ca pe arhitectură: soluția sus, apoi
  // corpul, vederea, nivelul și straturile. `cuVederi` e fals pe ecranele
  // goale, unde rămân soluția și corpul — un corp fără model trebuie să te
  // lase să treci la altul.
  // VEDERILE FĂRĂ DATE pe rândul privit (o schiță n-are analiză, încărcări
  // sau armare): butonul rămâne, gri, cu titlul care spune ce lipsește — nu
  // dispare, ca omul să vadă că vederea există și ce o aduce. Cât rezumatele
  // pașilor n-au sosit nu se știe nimic, deci nimic nu e gri.
  const areRezultat = (pas: string) =>
    !!steps[pas]?.result || !!rezumate?.[pas]?.are_rezultat;
  // ARMAREA: niciun pas de armare rulat pe rândul privit (o schiță, o macro
  // înaintea detaliului). Atunci componentele n-au ce număra, iar scena 3D
  // pornește în «Clasic» — realistul n-ar avea armătură de arătat. Cât
  // rezumatele n-au sosit nu se știe, deci nu se afirmă lipsa.
  const faraArmare = rezumate != null && !PASI_ARMARE.some(areRezultat);
  // …pe rândul «Geometrie macro» lipsa e firească: armarea rulează pe
  // «Calcul de detaliu». Titlul spune și unde e: rândul de detaliu al
  // macro-ului, dacă există deja, sau că trebuie pornit.
  const notaMacro = (): string => {
    if (solutiaCurenta?.faza !== "macro") return "";
    const detaliu = registru?.solutii.find(
      (r) => r.parent_solution_id === solutiaCurenta.id && r.faza === "detaliu");
    return " " + t(detaliu ? "struct.studio.gol_armare_macro"
                           : "struct.studio.gol_armare_macro_fara",
                   { macro: t("faza.macro"), detaliu: t("faza.detaliu") });
  };
  const faraDate = (v: VedereStructura): string | null => {
    if (rezumate == null) return null;
    if (v === "moduri" && !areRezultat("model_fem"))
      return t("struct.studio.gol_moduri");
    if (v === "incarcari" && !areRezultat("incarcari"))
      return t("struct.studio.gol_incarcari");
    // tipurile de secțiune vin din Pasul 4: pe un macro (fără armare)
    // vederea le arată, deci rămâne gri numai fără geometrie
    if (v === "componente" && faraArmare && !areRezultat("model_structura"))
      return t("struct.studio.gol_componente") + notaMacro();
    if (v === "plan_armare" && faraArmare)
      return t("struct.studio.gol_plan_armare") + notaMacro();
    if (v === "plan_cofraj" && !areRezultat("model_fem"))
      return t("struct.studio.gol_plan_cofraj");
    if (v === "plan_sapatura" && !areRezultat("model_fem"))
      return t("struct.studio.gol_plan_sapatura");
    return null;
  };
  const meniu = (cuVederi: boolean, cifre?: ReactNode) => {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const etaje2d: { nivel: string }[] =
      (steps["model_structura"]?.result as any)?.etaje ?? [];
    const areFundatie = !!(fundatiaAdoptata(steps) as any)?.latime_m;
    /* eslint-enable @typescript-eslint/no-explicit-any */
    const vedereEf = vedere2d ?? indexParter(etaje2d);
    return (
      <aside className="card bara-vederi meniu-stanga ob-studio-meniu">
        {blocSolutie}
        {grupCorp}
        {/* STRUCTURA FAȚADEI arhitecturii rândului: vântul pe anvelopă și
            cortina, pe pagina ei (`fatadaStructura/`) — un drum, nu o vedere */}
        {arhitecturaRandului && (
          <span className="grup-vederi">
            <button type="button" className="btn mic" title={t("struct.studio.fatada_structura_titlu")}
                    onClick={() => navigate(caleStructuraFatada(id, arhitecturaRandului))}>
              {t("struct.studio.fatada_structura")}
            </button>
          </span>
        )}
        <span className="grup-vederi">
          <span className="lbl">{t("viz.grup.vedere")}</span>
          <div className="meniu-vederi" role="group" aria-label={t("viz.grup.vedere")}>
            {VEDERI_STRUCTURA.map((v) => {
              const gol = faraDate(v);
              const on = vedere === v || (v === "model" && vedere === "fundatie");
              return (
                <button key={v} type="button"
                        className={[on ? "on" : "", gol ? "gol" : ""].join(" ").trim()}
                        aria-pressed={on}
                        disabled={!!gol && !on}
                        title={gol ?? (v === "moduri"
                          ? t("struct.studio.tab_moduri_titlu") : undefined)}
                        onClick={() => alegeVederea(v)}>
                  {t(`struct.studio.vedere.${v}` as const)}
                </button>
              );
            })}
          </div>
        </span>
        {cuVederi && tab === "model" && proiectie === "2d" && !toateCorpurile
          && (etaje2d.length > 1 || areFundatie) && (
          <span className="grup-vederi">
            <span className="lbl">{t("viz.grup.nivel")}</span>
            <select className="inp meniu-select" value={String(vedereEf)}
                    onChange={(e) => setVedere2d(e.target.value === "fundatie"
                      ? "fundatie" : Number(e.target.value))}>
              {etaje2d.map((e, i) => (
                <option key={i} value={i}>{e.nivel}</option>
              ))}
              {areFundatie && (
                <option value="fundatie">{t("struct.plan.fundatie_buton")}</option>
              )}
            </select>
          </span>
        )}
        {cuVederi && tab === "model" && (
          <span className="grup-vederi">
            <span className="lbl">{t("viz.grup.afisare")}</span>
            <div ref={setGazdaStraturi} key={proiectie}
                 className="meniu-straturi" />
          </span>
        )}
        {cifre}
      </aside>
    );
  };
  // fișa layerului selectat, în dreapta. Deschisă din start: tabul e o
  // explicație, iar o scenă colorată fără text lasă întrebarea „ce sunt
  // culorile astea" — exact cea la care fișa răspunde.
  const [fisa, setFisa] = useState(true);
  // UN CORP SĂRIT (lanțul lui s-a oprit, iar schița a mers mai departe fără
  // el) sau EXCLUS din calcul n-are model. Planșa lui spune de ce, în locul
  // scheletului de încărcare, iar vederea 3D nu se cere deloc: din Pasul 4
  // picat serverul n-o poate construi, deci cererea s-ar repeta la nesfârșit.
  const corpFaraModel = corpuri.find(
    (c) => c.activ && (c.stare === "sarit" || c.stare === "exclus")) ?? null;
  // MODELUL. Din Pasul 7 dacă a rulat; altfel din SCHIȚA lui — geometria
  // construită la cerere, fără nicio analiză (v. `schitaFem.ts`). Așa Studioul
  // arată clădirea și pe o soluție care s-a oprit înaintea Pasului 7 (schița de
  // structură) sau care abia a trecut de Pasul 4.
  const { schita, stare: stareSchita } = useSchitaFem(id, cloneId, steps,
                                                      rezumate, !corpFaraModel && !toateCorpurile);
  // «TOATE CORPURILE»: spațiul citit e al soluției, care n-are Pasul 7 (și
  // nici Pasul 4, deci nici schiță): se cere modelul COMPLEXULUI — corpurile
  // puse împreună pe server, construit la cerere ca și schița
  // (`modelComplex.ts`). Versiunea lui urmează Pasul 19 asamblat al soluției.
  const { model: complex, stare: stareComplex } = useModelComplex(
    id, solutiaCurenta?.id, !!toateCorpurile,
    rezumate?.["optimizare_structura"]?.updated_at ?? null);
  const r = (steps["model_fem"]?.result as FemResult | null | undefined)
    ?? schita ?? (toateCorpurile ? complex : null) ?? undefined;
  const eSchita = !steps["model_fem"]?.result && !!schita;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const incarcari = steps["incarcari"]?.result as any;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  // PLANUL 2D SE ADUCE DIN TIMP. Desenul parterului se interpretează pe
  // server la cerere și ține secunde bune; cerut abia la clicul pe «2D», omul
  // le aștepta pe toate atunci. Aici se cere după ce modelul 3D s-a așezat pe
  // ecran și Pasul 4 a sosit — cu o pauză, ca să nu-i ia banda modelului —
  // și stă în memoria desenelor (`fetchDrawingUrl`) până e nevoie de el.
  const pas4 = steps["model_structura"];
  // tipul («(tip SA)») și grupul de simetrie ale piesei alese vin din Pasul
  // 19, pe care planul de încărcare al Studioului îl aduce după model
  // (`STUDIO.apoi`); fără indexul ăsta capul panoului rămânea fără tip aici,
  // deși pagina pașilor îl arăta
  const clase = useMemo(
    () => indexClase(steps["optimizare_structura"]?.result), [steps]);
  const etajePas4 = (pas4?.result as { etaje?: unknown[] } | null)?.etaje;
  useEffect(() => {
    if (!r?.model || !etajePas4?.length) return;
    const t = setTimeout(() => {
      preincarcaDesen(
        stepDrawingUrl(id, "model_structura",
                       indexParter(etajePas4 as { nivel: string }[]),
                       cloneId ?? null),
        pas4?.updated_at);
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, cloneId, !!r?.model, pas4?.updated_at, !!etajePas4?.length]);
  // La fel formele modale (tabul «Moduri»): partea grea a Pasului 7, lăsată
  // deoparte de server ca modelul să sosească repede, se aduce după el.
  const versiuneFem = steps["model_fem"]?.updated_at;
  useEffect(() => {
    if (!r?.model || !r.verificare_modala?.forme_separat) return;
    const t = setTimeout(() => preincarcaForme(id, cloneId ?? null, versiuneFem), 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, cloneId, !!r?.model, versiuneFem]);

  // pașii piesei alese: planul Studioului nu aduce armările, deci verdictul și
  // costul din drawer vin abia cu cererea asta (v. `pasiSelectie`)
  const pasiSel = useMemo(() => (r && !toateCorpurile ? pasiSelectie(sel, r) : []),
                          [sel, r, toateCorpurile]);
  useEffect(() => {
    if (pasiSel.length) cerePasi(pasiSel);
  }, [pasiSel, cerePasi, cloneId]);
  const pasiSelPeDrum = pasiSel.some(
    (k) => !steps[k]?.result && (rezumate == null || !!rezumate[k]?.are_rezultat));
  // dublu-click în 3D: pagina piesei, dacă are una
  const deschide = (s: SelectieFem) => {
    if (!r) return;
    if (toateCorpurile) {
      // pe complex piesa e a unui corp: pagina ei stă pe spațiul lui
      const p = piesaComplex(r, s);
      if (p?.ref && p.corp !== CORP_PARCARE) {
        navigate(hashElement(id, p.ref, refLegatura(solutiaCurenta?.id, p.corp)));
      }
      return;
    }
    const ref = pasiSelectie(s, r).length ? refDinSelectie(r, s) : null;
    if (ref) navigate(hashElement(id, ref, solutie ?? null));
  };

  // EDITORUL, în locul planșei: pe sursa privită, cu soluția ei în capul
  // meniului. «Salvează ca Soluția N+1» duce pe rândul nou (A8).
  if (editez && solutiaCurenta?.status === "ready") {
    return (
      <EditorStructura
        key={solutiaCurenta.id}
        projectId={id} sursa={solutiaCurenta.id}
        corp={corpuri.find((c) => c.activ)?.cod ?? null}
        laInchide={() => setEditez(false)}
        laSalvat={async (r) => {
          // A8: rândul nou se deschide selectat, iar anunțul spune din ce s-a
          // născut. Registrul se recitește ÎNAINTE: fără el, ecranul ar căuta
          // Soluția N+1 într-un registru care încă n-o are.
          const sursa = registru ? elementulRandului(registru.solutii, solutiaCurenta.id) : null;
          await reciteste();
          setEditez(false);
          if (sursa) {
            anunta({
              id: `editata:${r.macro.id}`,
              text: t("anunt.editata", { nume: numeRand(r.macro),
                                         din: numeElement(sursa) }),
              href: caleRand(id, r.macro),
            });
          }
          navigate(caleRand(id, r.macro));
        }} />
    );
  }

  // Vederile fără planșă: componentele, lanțul de calcul, setările de calcul.
  // Stau în dreapta meniului, pe fondul studioului.
  if (vedere === "componente" || vedere === "plan_armare" || vedere === "plan_cofraj"
      || vedere === "plan_sapatura" || vedere === "lant" || vedere === "setari") {
    return (
      <div className="ob-studio-wrap">
        {meniu(false)}
        <div className="ob-studio">
          <div className="ob-studio-panou ob-studio-document">
            {vedere === "componente" && (
              <VedereComponente projectId={id} cloneId={cloneId ?? null}
                                solutie={solutie ?? null} />
            )}
            {vedere === "plan_armare" && (
              <PlanArmare projectId={id} cloneId={cloneId ?? null}
                          solutie={solutie ?? null} />
            )}
            {vedere === "plan_cofraj" && (
              <PlanCofraj projectId={id} cloneId={cloneId ?? null}
                          solutie={solutie ?? null} />
            )}
            {vedere === "plan_sapatura" && (
              <PlanSapatura projectId={id} cloneId={cloneId ?? null}
                            solutie={solutie ?? null} />
            )}
            {vedere === "lant" && (
              <PipelinePage
                id={id}
                solutie={solutie}
                steps={steps}
                runningAll={!!actiuni.ocupat}
                runError={actiuni.eroare ?? runError}
                etichetaRulare={actiuneLant?.fel ?? null}
                ruleaza={!!solutiaCurenta?.rulare}
                onRunAll={pornesteLantul}
                onStop={() => { void opreste(); }}
                lant={lant}
              />
            )}
            {vedere === "setari" && <Preferinte projectId={id} />}
          </div>
        </div>
      </div>
    );
  }

  // abia după vederile fără planșă: lanțul corpului sărit rămâne de citit, și
  // arată pasul la care s-a oprit
  if (corpFaraModel) {
    return (
      <div className="ob-studio-wrap">
        {meniu(false)}
        <div className="ob-studio">
          <div className="ob-studio-gol">
            <div style={{ maxWidth: 560 }}>
              <p>
                {t(corpFaraModel.stare === "sarit" ? "struct.studio.corp_sarit"
                   : "struct.studio.corp_exclus", { cod: corpFaraModel.cod })}
              </p>
              {corpFaraModel.motiv && (
                <p>{t("struct.studio.corp_motiv", { motiv: traduceRezultat(corpFaraModel.motiv) })}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Trei stări, nu două. Rezultatul Pasului 7 are megaocteți și sosește după
  // rezumatele pașilor; până atunci ecranul ARĂTA «Modelul 3D apare după
  // rularea lanțului» — adevărat doar când modelul chiar lipsește. Cât e pe
  // drum, locul lui îl ține scheletul clădirii, la aceeași mărime și din
  // același unghi ca modelul care vine.
  if (stareModel(steps["model_fem"], rezumate) === "se_incarca"
      || (!r && stareSchita === "se_incarca")
      || (!r && toateCorpurile && stareComplex === "se_incarca")) {
    return (
      <div className="ob-studio-wrap">
        {meniu(false)}
        <div className="ob-studio">
          <ScheletIncarcare silueta={silueta} />
        </div>
      </div>
    );
  }
  if (!r || !r.model) {
    return (
      <div className="ob-studio-wrap">
        {meniu(false)}
        <div className="ob-studio">
          <div className="ob-studio-gol">
            <div>
              <p>
                {t(toateCorpurile ? "struct.studio.gol_complex" : "struct.studio.gol_model")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }


  const cazuri: string[] = (incarcari?.aplicare ?? []).map(
    (a: { caz: string }) => a.caz
  );
  // legenda cazurilor (Pasul 8): din ea se iau natura acțiunii — deci
  // grupurile de chips — și textele fișei. Lipsește pe soluțiile rulate
  // înaintea ei; atunci chips-urile rămân un singur grup, fără fișă.
  const legenda: CazLegenda[] = incarcari?.legenda_cazuri ?? [];
  const limite: AvertismentMetoda[] = incarcari?.metoda ?? [];
  const maseNivel: MasaNivel[] = incarcari?.mase_detaliu
    ?? r.mase_detaliu ?? [];
  const numeCaz = (c: string) =>
    legenda.find((x) => x.caz === c)?.nume
    ?? t("struct.studio.caz_generic", { caz: c });
  const grupuriCazuri = legenda.length
    ? [
        { eticheta: t("struct.studio.permanente"), tip: "permanenta" },
        { eticheta: t("struct.studio.variabile"), tip: "variabila" },
      ].map((g) => ({
        eticheta: g.eticheta,
        cazuri: cazuri.filter(
          (c) => legenda.find((x) => x.caz === c)?.tip === g.tip),
      })).filter((g) => g.cazuri.length)
    : [{ eticheta: t("struct.studio.cazuri"), cazuri }];

  // cifrele vederii 2D — citite AICI, fiindcă bara de jos e una singură pentru
  // ambele proiecții (StudioPlan nu-și mai plutește propriul grup de chips)
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const mstr = steps["model_structura"]?.result as any;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  // pe o SCHIȚĂ regulile de amplasare nu s-au verificat, deci chipul lor nu
  // are ce spune (v. `verificari_amplasare.nerulata`); cifrele geometriei
  // rămân, ele există
  const amplasareRulata = !mstr?.verificari_amplasare?.nerulata;
  const ms = mstr
    ? {
        reguli: (mstr.verificari_amplasare?.reguli ?? []).length as number,
        incalcate: (mstr.verificari_amplasare?.reguli ?? []).filter(
          (x: { ok: boolean }) => !x.ok
        ).length as number,
        nrStalpi: (mstr.nr_stalpi_total ?? 0) as number,
        nrGrinzi: (mstr.nr_grinzi_total ?? 0) as number,
        regim: (mstr.regim_inaltime ?? "") as string,
      }
    : null;

  // cifrele FUNDAȚIEI, pentru bara vederii 2D: acolo unde înainte era tabul, e
  // acum un chip — geometria adoptată de Pasul 17 și verdictul pe axe, cu
  // drum spre pagina lui. Verdictele globale (tasare, ridicare) stau tot acolo.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const rf = fundatiaAdoptata(steps) as any;
  const pasFundatie = cheiaFundatiei(steps);
  /* eslint-enable @typescript-eslint/no-explicit-any */
  const axeF: AxFundatie[] = rf?.verificari?.axe ?? [];
  const fund = axeF.length
    ? {
        latime: rf.latime_m as number | undefined,
        adancime: rf.adancime_m as number | undefined,
        nr: axeF.length,
        picate: axeF.filter((a) => verdictAx(a) === "bad").length,
        limita: axeF.filter((a) => verdictAx(a) === "amb").length,
      }
    : null;

  // drawer-ul de element apare doar peste modelul 3D; în 2D panoul lateral e
  // al vederii înseși (StudioPlan, cu drawer-ul de ax de fundație). Complexul
  // n-are plan (Pasul 4 e al fiecărui corp), deci e mereu 3D.
  const arata3D = tab === "model" && (proiectie === "3d" || !!toateCorpurile);
  const arataDrawer = arata3D && !!sel;


  // CIFRELE VEDERII, în capătul meniului, ca pe arhitectură: aceleași
  // pastile-link de până acum (verdictul și drumul spre pasul lui), una sub
  // alta în loc de un rând plutitor jos.
  // Pe 3D colțul meniului rămâne gol (fără «geometrie · fără analiză», masa
  // și legătura spre Pasul 7) — cifrele sunt ale planului.
  const cifre = tab === "model" && proiectie === "2d" && !toateCorpurile && (
    <div className="meniu-cifre">
      {(
        <>
          {ms && amplasareRulata && (
            <a
              className={`ob-chip ${ms.incalcate ? "ob-c-bad" : "ob-c-ok"}`}
              style={{ boxShadow: "var(--sh)", textDecoration: "none" }}
              href={hashSectiune(id, "verificari", solutie ?? null)}
              title={t("struct.studio.reguli_titlu")}
            >
              {ms.incalcate
                ? t("struct.studio.reguli_incalcate",
                    { n: ms.incalcate, total: ms.reguli })
                : t("struct.studio.reguli_trec", { n: ms.reguli })}
            </a>
          )}
          {ms && (
            <span className="ob-chip ob-c-mut" style={{ boxShadow: "var(--sh)" }}>
              {t("struct.studio.stalpi_grinzi", {
                stalpi: nrRo(ms.nrStalpi), grinzi: nrRo(ms.nrGrinzi),
              })}
              {ms.regim ? ` · ${ms.regim}` : ""}
            </span>
          )}
          {fund && (
            <a
              className={`ob-chip ${
                fund.picate ? "ob-c-bad" : fund.limita ? "ob-c-amb" : "ob-c-ok"
              }`}
              style={{ boxShadow: "var(--sh)", textDecoration: "none" }}
              href={hashSectiune(id, pasFundatie, solutie ?? null)}
              title={t("struct.studio.fund_titlu")}
            >
              {t("struct.studio.fund", {
                B: nrRo(fund.latime ?? 0, 2),
                D: nrRo(fund.adancime ?? 0, 2),
              })}
              {fund.picate
                ? t("struct.studio.axe_pica", { n: fund.picate, total: fund.nr })
                : fund.limita
                  ? t("struct.studio.axe_limita", { n: fund.limita })
                  : t("struct.studio.axe_trec", { n: fund.nr })}
            </a>
          )}
          <a
            className="ob-chip ob-c-mut"
            style={{ boxShadow: "var(--sh)", textDecoration: "none" }}
            href={hashSectiune(id, "model_structura", solutie ?? null)}
            title={t("struct.studio.p4_titlu")}
          >
            {t("struct.studio.p4")}
          </a>
        </>
      )}
    </div>
  );

  return (
    <div className="ob-studio-wrap">
    {meniu(true, cifre)}
    <div className="ob-studio">
      {/* 2D / 3D în colțul din dreapta-jos, ca pe situație și arhitectură —
          v. `comutatorVedere` în `viewer/Vizualizator`. */}
      {tab === "model" && !toateCorpurile && (
        <div className="float colt-dreapta-jos">
          <div className="card colt-vedere">
            <div className="tabs niveluri">
              <button className={proiectie === "2d" ? "on" : ""}
                      onClick={() => setProiectie("2d")}
                      title={t("struct.studio.2d_titlu")}>2D</button>
              <button className={proiectie === "3d" ? "on" : ""}
                      onClick={() => setProiectie("3d")}
                      title={t("struct.studio.3d_titlu")}>3D</button>
              {/* MACHETA UNIFICATĂ (`viewer/macheta`): situația cu fațade, cu
                  structura rândului în ea — pagina ei, pe rândul curent */}
              {solutiaCurenta && (
                <button type="button" onClick={() => navigate(`/p/${id}/structura/${solutiaCurenta.id}/macheta`)}
                        title={t("struct.studio.macheta_titlu")}>{t("struct.studio.macheta")}</button>
              )}
            </div>
          </div>
        </div>
      )}
      {arata3D && <Scene3D r={r} sel={sel} onSel={setSel} onDeschide={deschide}
                          projectId={id}
                          cloneId={cloneId ?? null} straturiIn={gazdaStraturi}
                          versiune={steps["model_fem"]?.updated_at}
                          faraArmare={faraArmare} />}
      {tab === "model" && proiectie === "2d" && !toateCorpurile && (
        <StudioPlan id={id} cloneId={cloneId ?? null} solutie={solutie} steps={steps}
                    vedere={vedere2d ?? indexParter(
                      ((pas4?.result as { etaje?: { nivel: string }[] } | null)
                        ?.etaje) ?? [])}
                    laVedere={setVedere2d} straturiIn={gazdaStraturi} />
      )}
      {tab === "moduri" && (eSchita ? (
        <div className="ob-studio-gol">
          <div>
            <p>
              {t("struct.studio.gol_moduri")}
            </p>
            <a href={hashSectiune(id, "pipeline", solutie ?? null)}>
              {t("struct.studio.deschide_pipeline")}
            </a>
          </div>
        </div>
      ) : (
        <div className="ob-studio-panou">
          <FormeModale r={r} projectId={id} cloneId={cloneId}
                       versiune={steps["model_fem"]?.updated_at} />
        </div>
      ))}
      {tab === "incarcari" &&
        (incarcari?.aplicare?.length && r.model ? (
          <div className="ob-studio-panou" style={{ paddingTop: 54 }}>
            {/* cazurile, GRUPATE pe natura acțiunii: G, Q și masa se combină
                diferit, deci nici la ales nu stau într-un șir unic. Click pe
                un chip = scena aprinde suprafețele lui ȘI fișa din dreapta
                spune ce e. */}
            <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 12 }}>
              {grupuriCazuri.map((g) => (
                <div key={g.eticheta} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="ob-lbl" style={{ width: 78, flex: "none" }}>
                    {g.eticheta}
                  </span>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {g.cazuri.map((c) => (
                      <button
                        key={c}
                        className={`ob-chip ${
                          mod3d.fel === "incarcare" && mod3d.caz === c
                            ? "ob-c-acc" : "ob-c-mut"
                        }`}
                        style={{ cursor: "pointer", border: "none", font: "inherit", fontSize: 11 }}
                        onClick={() => { setMod3d({ fel: "incarcare", caz: c }); setFisa(true); }}
                        title={numeCaz(c)}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="ob-lbl" style={{ width: 78, flex: "none" }}>
                  {t("struct.studio.seism")}
                </span>
                <button
                  className={`ob-chip ${mod3d.fel === "masa" ? "ob-c-acc" : "ob-c-mut"}`}
                  style={{ cursor: "pointer", border: "none", font: "inherit", fontSize: 11 }}
                  onClick={() => { setMod3d({ fel: "masa" }); setFisa(true); }}
                  title={t("struct.studio.masa_titlu")}
                >
                  {t("struct.studio.masa")}
                </button>
              </div>
            </div>
            <Incarcari3D
              model={r.model}
              aplicare={incarcari.aplicare}
              mod={mod3d}
              mase={r.model.mase ?? []}
              masaInfra={r.model.masa_infrastructura ?? []}
              height={620}
            />
            <div className="sg-muted" style={{ fontSize: 12, marginTop: 8 }}>
              {t("struct.studio.detalii_incarcari")}
              <a href={hashSectiune(id, "incarcari", solutie ?? null)}>
                {t("struct.studio.pagina_p8")}
              </a>
            </div>
          </div>
        ) : (
          <div className="ob-studio-gol">
            <div>
              <p>{t("struct.studio.gol_incarcari")}</p>
              <a href={hashSectiune(id, "pipeline", solutie ?? null)}>
                {t("struct.studio.pipeline")}
              </a>
            </div>
          </div>
        ))}


    </div>

      {/* DRAWER-ul de element: pe toată înălțimea, în dreapta — canvasul se
          restrânge, ca panoul să nu acopere clădirea (varianta B a planșei de
          design, Stefan). Sus identitatea + verdictele + costul cu
          rețeta (StudioSumar), dedesubt proprietățile de calcul. */}
      {/* FIȘA LAYERULUI selectat în tabul «Încărcări»: același drawer, alt
          conținut — ce e cazul, din ce cifră iese, pe ce articol stă, cât cade
          pe fiecare planșeu și ce nu e în model. */}
      {tab === "incarcari" && fisa && !!incarcari?.aplicare?.length
        && (legenda.length || mod3d.fel === "masa") && (
        <FisaIncarcare
          mod={mod3d}
          legenda={legenda}
          limite={limite}
          mase={maseNivel}
          masaInfraT={incarcari?.masa_infrastructura_t ?? null}
          linkPas8={hashSectiune(id, "incarcari", solutie ?? null)}
          linkSeism={hashSectiune(id, "seism", solutie ?? null)}
          onInchide={() => setFisa(false)}
        />
      )}

      {arataDrawer && (
        <aside className="ob-studio-inspector">
          {toateCorpurile ? (
            <CardCorpComplex id={id} solutie={solutiaCurenta?.id ?? null} r={r} sel={sel}
                             onClose={() => setSel(null)} />
          ) : (
            <StudioSumar
              id={id}
              cloneId={cloneId ?? null}
              solutie={solutie}
              r={r}
              sel={sel}
              steps={steps}
              seIncarca={pasiSelPeDrum}
              onClose={() => setSel(null)}
            />
          )}
          <PanouProprietati r={r} sel={sel} clase={clase} onClose={() => setSel(null)} />
        </aside>
      )}
    </div>
  );
}
